import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import {
  Activity,
  CheckCircle2,
  Clock,
  Database,
  Gauge,
  AlertTriangle,
  Pause,
  Library,
  Scale,
} from "lucide-react";

const fmt = (n) =>
  typeof n === "number" ? n.toLocaleString("en-US") : n ?? "—";

const fmtEta = (h, isAr) => {
  if (h === null || h === undefined) return isAr ? "غير محدّد" : "n/a";
  if (h < 1) {
    const m = Math.round(h * 60);
    return isAr ? `~${m} د` : `~${m} min`;
  }
  if (h < 48) return isAr ? `~${Math.round(h)} ساعة` : `~${Math.round(h)} h`;
  const d = Math.round(h / 24);
  return isAr ? `~${d} يوم` : `~${d} day${d === 1 ? "" : "s"}`;
};

const fmtTime = (iso, isAr) => {
  if (!iso) return isAr ? "لا توجد بيانات" : "no data";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return isAr ? `قبل ${Math.round(diff)} ث` : `${Math.round(diff)}s ago`;
  if (diff < 3600) return isAr ? `قبل ${Math.round(diff / 60)} د` : `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return isAr ? `قبل ${Math.round(diff / 3600)} س` : `${Math.round(diff / 3600)}h ago`;
  return isAr ? `قبل ${Math.round(diff / 86400)} ي` : `${Math.round(diff / 86400)}d ago`;
};

export default function SamiTrainingProgress() {
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/sami/training-progress");
      setData(data);
      setErr(false);
    } catch {
      setErr(true);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000); // refresh every 30 s
    return () => clearInterval(id);
  }, [load]);

  if (err) {
    return (
      <div
        data-testid="sami-progress-error"
        className="border border-red-200 bg-red-50 p-5 text-red-700 text-sm"
      >
        {isAr ? "تعذّر تحميل لوحة تقدّم سامي." : "Could not load Sami progress."}
      </div>
    );
  }

  if (!data) {
    return (
      <div
        data-testid="sami-progress-loading"
        className="border border-slate-200 bg-white p-5 text-slate-500 text-sm"
      >
        {isAr ? "جاري تحميل لوحة تقدّم سامي..." : "Loading Sami progress..."}
      </div>
    );
  }

  const {
    total,
    answered,
    pending,
    progress_pct: pct,
    answered_last_hour: lh,
    answered_last_24h: l24,
    rate_per_hour: rate,
    eta_hours: eta,
    last_answered_at: lastAt,
    worker_running: running,
    laws_count: laws,
    judgments_count: judgments,
  } = data;

  const stale = !running && pending > 0;

  return (
    <div
      data-testid="sami-progress-dashboard"
      className="border border-slate-200 bg-white p-6 lg:p-8 mb-8"
    >
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-slate-500" />
            <p className="eyebrow">
              {isAr ? "تقدّم تدريب سامي" : "Sami training progress"}
            </p>
          </div>
          <h3
            data-testid="sami-progress-headline"
            className="font-display text-3xl lg:text-4xl mt-2 tracking-tight"
          >
            {fmt(answered)} / {fmt(total)}{" "}
            <span className="text-slate-400 text-2xl">({pct}%)</span>
          </h3>
        </div>

        <div className="flex items-center gap-2 mt-2">
          {running ? (
            <span
              data-testid="sami-worker-running"
              className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium"
            >
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
              {isAr ? "العامل يعمل" : "Worker running"}
            </span>
          ) : (
            <span
              data-testid="sami-worker-stopped"
              className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium"
            >
              <Pause className="w-3 h-3" />
              {isAr ? "العامل متوقف" : "Worker stopped"}
            </span>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-6">
        <div
          data-testid="sami-progress-bar"
          className="h-full bg-slate-900 transition-all duration-500"
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <Tile
          icon={<CheckCircle2 className="w-3.5 h-3.5" />}
          label={isAr ? "مُجاب" : "Answered"}
          value={fmt(answered)}
          testid="stat-answered"
        />
        <Tile
          icon={<Clock className="w-3.5 h-3.5" />}
          label={isAr ? "متبقّي" : "Pending"}
          value={fmt(pending)}
          testid="stat-pending"
        />
        <Tile
          icon={<Gauge className="w-3.5 h-3.5" />}
          label={isAr ? "آخر ساعة" : "Last hour"}
          value={fmt(lh)}
          testid="stat-last-hour"
        />
        <Tile
          icon={<Gauge className="w-3.5 h-3.5" />}
          label={isAr ? "آخر 24س" : "Last 24h"}
          value={fmt(l24)}
          testid="stat-last-24h"
        />
        <Tile
          icon={<Activity className="w-3.5 h-3.5" />}
          label={isAr ? "معدّل/ساعة" : "Rate/hr"}
          value={fmt(rate)}
          testid="stat-rate"
        />
        <Tile
          icon={<Clock className="w-3.5 h-3.5" />}
          label={isAr ? "الإكمال المتوقّع" : "ETA"}
          value={fmtEta(eta, isAr)}
          testid="stat-eta"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <Tile
          icon={<Library className="w-3.5 h-3.5" />}
          label={isAr ? "القوانين" : "Laws"}
          value={fmt(laws)}
          testid="stat-laws"
          tone="muted"
        />
        <Tile
          icon={<Scale className="w-3.5 h-3.5" />}
          label={isAr ? "الأحكام" : "Judgments"}
          value={fmt(judgments)}
          testid="stat-judgments"
          tone="muted"
        />
        <Tile
          icon={<Database className="w-3.5 h-3.5" />}
          label={isAr ? "آخر إجابة" : "Last answer"}
          value={fmtTime(lastAt, isAr)}
          testid="stat-last-answer"
          tone="muted"
        />
      </div>

      {stale && (
        <div
          data-testid="sami-worker-warning"
          className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-200 text-amber-900 text-sm"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p className="leading-relaxed">
            {isAr
              ? "العامل الخلفي متوقف بينما لا تزال هناك أسئلة معلّقة. أعِد تشغيله بتنفيذ: "
              : "Background worker is stopped while questions remain. Restart with: "}
            <code className="px-1.5 py-0.5 bg-amber-100 font-mono text-xs">
              cd /app/backend && nohup python3 -m scripts.answer_pending_qa --concurrency 3 --target 0 &gt; /tmp/answer_pending.log 2&gt;&amp;1 &amp;
            </code>
          </p>
        </div>
      )}

      {lh === 0 && running && pending > 0 && (
        <div
          data-testid="sami-progress-idle"
          className="flex items-start gap-2.5 p-3 bg-blue-50 border border-blue-200 text-blue-900 text-sm mt-3"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p className="leading-relaxed">
            {isAr
              ? "العامل يعمل لكن لم تُسجَّل أي إجابة في آخر ساعة. تحقّق من رصيد Universal Key (Profile → Universal Key → Add Balance) أو من الـ Rate Limits."
              : "Worker is running but no answers logged in the past hour. Check your Universal Key balance (Profile → Universal Key → Add Balance) or LLM rate limits."}
          </p>
        </div>
      )}
    </div>
  );
}

function Tile({ icon, label, value, testid, tone = "default" }) {
  const bg = tone === "muted" ? "bg-slate-50" : "bg-white";
  const border = tone === "muted" ? "border-slate-100" : "border-slate-200";
  return (
    <div
      data-testid={testid}
      className={`border ${border} ${bg} px-3 py-2.5`}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-500">
        {icon}
        <span>{label}</span>
      </div>
      <div className="font-display text-lg lg:text-xl mt-1 tracking-tight text-slate-900">
        {value}
      </div>
    </div>
  );
}
